/**
 * The list of connected stores, and the first step of the onboarding wizard.
 *
 * A brand-new account lands here with nothing, so the empty state is not a
 * consolation message — it is the primary interface, and the form is already
 * on screen rather than behind a button.
 */

import { Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";

import { useCreateSite, useSites } from "../api/queries";
import type { Site } from "../api/types";
import { useCurrentOrganization } from "../auth/session";
import {
  Badge,
  Button,
  Card,
  ErrorNote,
  Field,
  Input,
  Loading,
} from "../ui/primitives";

export function SitesPage() {
  const organization = useCurrentOrganization();
  const orgId = organization?.organization.id ?? "";
  const sites = useSites(orgId);

  if (!organization) return <Loading />;
  if (sites.isLoading) return <Loading label="Loading your sites…" />;
  if (sites.error) return <ErrorNote error={sites.error} />;

  const rows = sites.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-lg font-semibold">Your sites</h1>
          <p className="text-sm text-muted">
            Each site is one store with its own assistant, content and widget.
          </p>
        </div>
      </div>

      {rows.length === 0 ? (
        <FirstSite orgId={orgId} />
      ) : (
        <>
          <ul className="grid gap-3 sm:grid-cols-2">
            {rows.map((site) => (
              <li key={site.id}>
                <SiteCard site={site} />
              </li>
            ))}
          </ul>
          <AddSite orgId={orgId} />
        </>
      )}
    </div>
  );
}

function SiteCard({ site }: { site: Site }) {
  return (
    <Link
      to="/sites/$siteId"
      params={{ siteId: site.id }}
      className="block rounded-xl border border-line bg-raised p-4 transition hover:border-brand"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-medium">{site.name}</div>
          <div className="truncate text-xs text-muted">{site.url}</div>
        </div>
        <StatusBadge status={site.status} />
      </div>
    </Link>
  );
}

export function StatusBadge({ status }: { status: string }) {
  if (status === "connected") return <Badge tone="good">Connected</Badge>;
  if (status === "disabled") return <Badge tone="bad">Disabled</Badge>;
  // "pending" means the plugin has not completed pairing. Named for what the
  // customer has to do about it, not for the database value.
  return <Badge tone="warn">Not connected</Badge>;
}

function FirstSite({ orgId }: { orgId: string }) {
  return (
    <Card title="Connect your first store">
      <p className="mb-4 text-sm text-muted">
        Add the store you want the assistant to answer for. You will install the
        WordPress plugin — or paste one script tag — in the next step.
      </p>
      <SiteForm orgId={orgId} cta="Add store" />
    </Card>
  );
}

function AddSite({ orgId }: { orgId: string }) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <Button onClick={() => setOpen(true)}>Add another site</Button>
    );
  }

  return (
    <Card title="Add a site">
      <SiteForm orgId={orgId} cta="Add site" onDone={() => setOpen(false)} />
    </Card>
  );
}

function SiteForm({
  orgId,
  cta,
  onDone,
}: {
  orgId: string;
  cta: string;
  onDone?: () => void;
}) {
  const create = useCreateSite(orgId);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    await create.mutateAsync({ name, url });
    setName("");
    setUrl("");
    onDone?.();
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      {create.error != null && <ErrorNote error={create.error} />}

      <Field label="Store name" id="site-name">
        <Input
          id="site-name"
          required
          placeholder="Bristol Coffee"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </Field>

      <Field
        label="Store address"
        id="site-url"
        hint="The address visitors use. Must be https."
      >
        <Input
          id="site-url"
          type="url"
          required
          placeholder="https://shop.example.com"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
        />
      </Field>

      <Button type="submit" variant="primary" disabled={create.isPending}>
        {create.isPending ? "Adding…" : cta}
      </Button>
    </form>
  );
}
